/* eslint-disable class-methods-use-this */
import React, { createRef } from 'react';
import store from 'store/Store';
import FrameworkUtils from 'utils/framework';
import { css, Global } from '@emotion/react';
import { Icon } from 'components/ui';
import scriptUtil from 'utils/LoadScripts';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import mediaUtils from 'utils/Media';
import LanguageLocale from 'utils/LanguageLocale';
import {
    colors, mediaQueries, fontSizes, lineHeights, fontWeights, space, radii
} from 'style/config';
import Storage from 'utils/localStorage/Storage';
import Location from 'utils/Location';
import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import SuperChatActions from 'ai/actions/superChatActions';
import { CHAT_ENTRY } from 'constants/chat';
import gladApi from 'services/api/gladChat';
import userUtils from 'utils/User';
import ProactiveGreeting from 'components/GladChat/ProactiveGreeting';
import gladChatUtils from 'utils/gladChat';
import BaseClass from 'components/BaseClass/BaseClass';
import ChatButtonENG from 'components/GladChat/ChatButton/locales/ChatButton_en_US.js';
import ChatButtonFR from 'components/GladChat/ChatButton/locales/ChatButton_fr_CA.js';

const { wrapComponent } = FrameworkUtils;
const { Media } = mediaUtils;
const TRANS_FORM_Y = space[7] + space[2];

const { getLocalisedDataFromResource } = LanguageLocale;
const getText = getLocalisedDataFromResource(ChatButtonENG, ChatButtonFR);

class GladChat extends BaseClass {
    _pendingShowToken = null;

    constructor(props) {
        super(props);
        this.state = {
            unreadMessage: 0,
            isHandleShowClicked: false,
            proActiveChat: {
                showGreeting: false,
                campaignId: '',
                action: null,
                campaignOpen: false
            }
        };
        this.gladRef = createRef();
        this.gladChatEntry = createRef();
        this.conversationTimeout = null;
    }

    componentDidMount() {
        const { isChatOpen = false, conversationEnd, unreadMessage } = this.getStorageItem(LOCAL_STORAGE.GLADLY_CHAT);

        if (unreadMessage) {
            this.setState({ unreadMessage });
        }

        if (!window.Gladly) {
            scriptUtil.loadScripts(['/js/ufe/isomorphic/thirdparty/gladchat.js'], () => {
                const selectedAppId = gladChatUtils.getAppId();

                if (window.Gladly && selectedAppId) {
                    this.initializeGladChat(selectedAppId, isChatOpen, conversationEnd);
                }
            });
        }

        window.addEventListener('popstate', this.handleClose);
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.handleClose);
        this._pendingShowToken = null;
    }

    getStorageItem = key => {
        return Storage.local?.getItem(key) || {};
    };

    setStorageItem = (value, key) => {
        const gladChat = this.getStorageItem(key);
        Storage.local.setItem(key, { ...gladChat, ...value });
    };

    fireAnalytics = (eventData, callType) => {
        processEvent.process(callType, {
            data: eventData
        });
    };

    afterInitilizeGladChat = () => {
        store.setAndWatch('gladChat', this, data => {
            const current = this.getStorageItem(LOCAL_STORAGE.GLADLY_CHAT);

            if (data.gladChat.clickPage) {
                let eventData = null;
                let callType = null;

                switch (data.gladChat.clickPage) {
                    case CHAT_ENTRY.footer:
                        eventData = {
                            pageName: `${anaConsts.PAGE_NAMES.GLAD_FOOTER_PAGE}:n/a:*`,
                            eventStrings: [anaConsts.Event.EVENT_220],
                            linkData: anaConsts.LinkData.CUSTOMER_CHAT_SESSION_STARTED,
                            navigationInfo: `toolbar nav:${anaConsts.PAGE_DETAIL.GLAD_PAGE}: ${anaConsts.PAGE_DETAIL.GLAD_PAGE}: ${anaConsts.PAGE_DETAIL.GLAD_PAGE}: ${anaConsts.PAGE_DETAIL.GLAD_PAGE}`
                        };
                        callType = anaConsts.ASYNC_PAGE_LOAD;

                        break;
                    case CHAT_ENTRY.customerService:
                        eventData = {
                            pageName: `${anaConsts.PAGE_NAMES.GLAD_FOOTER_PAGE}:n/a:*`,
                            eventStrings: [anaConsts.Event.EVENT_220],
                            linkData: anaConsts.LinkData.CUSTOMER_CHAT_SESSION_STARTED
                        };
                        callType = anaConsts.ASYNC_PAGE_LOAD;

                        break;
                    default:
                        break;
                }

                if (eventData) {
                    this.fireAnalytics(eventData, callType);
                }
            }

            if (data.gladChat.entryFlag) {
                this.handleShow();
            }

            if (data.gladChat.isChatOpen) {
                !current?.isChatOpen && this.setStorageItem({ isChatOpen: true }, LOCAL_STORAGE.GLADLY_CHAT);
            }
        });
        store.setAndWatch('page', this, data => {
            const {
                page: { showLoadSpaPageProgress }
            } = data;
            const element = this.gladChatEntry.current;

            if (!showLoadSpaPageProgress) {
                const isBasketPage = Location.isBasketPage();
                const height = this.basketCheckoutHeightSmui(isBasketPage);
                element?.parentElement.style.setProperty('--custom-bottom-value', `calc(var(--bottomNavHeight, 0px) + ${height + space[2]}px)`);
            }
        });

        store.setAndWatch('user.login', this, ({ login }) => {
            const isNotMinimized = gladChatUtils.gladAppisNotMinimized();
            const { profileStatus } = store.getState().auth;
            const isEligible = isNotMinimized && !this.state.isHandleShowClicked && !userUtils.isAnonymousOrRecognizedNotSSI({ profileStatus });
            (isEligible || !login) && this.validateUserAndSet();
        });

        // Re-validate when profileStatus changes — handles the race condition where
        // user.login arrives before profileStatus is updated to RECOGNIZED_SSI (3).
        // Without this, Level 3 users stay unauthenticated in Gladly and get prompted
        // for their phone number.
        store.setAndWatch('auth.profileStatus', this, ({ profileStatus }) => {
            const isNotMinimized = gladChatUtils.gladAppisNotMinimized();

            if (isNotMinimized && !this.state.isHandleShowClicked && !userUtils.isAnonymousOrRecognizedNotSSI({ profileStatus })) {
                this.validateUserAndSet();
            }
        });

        if (Sephora.configurationSettings.isGenAIChatEnabledUFE) {
            store.setAndWatch('superChat.showSuperChat', this, ({ showSuperChat }) => {
                const isNotMinimized = gladChatUtils.gladAppisNotMinimized();

                if (showSuperChat && isNotMinimized) {
                    this.handleClose();
                }
            });
        }

        try {
            Gladly?.on('conversation:started', started => {
                if (started) {
                    clearTimeout(this.conversationTimeout);
                    this.conversationTimeout = null;
                    this.setStorageItem({ conversationEnd: null }, LOCAL_STORAGE.GLADLY_CHAT);
                }
            });

            Gladly?.on('conversation:ended', ended => {
                if (ended) {
                    const conversationEnd = Date.now();
                    this.setStorageItem({ conversationEnd }, LOCAL_STORAGE.GLADLY_CHAT);
                    this.timeOutHandler(conversationEnd);

                    if (this.gladRef.current?.classList?.contains('gladly-show')) {
                        const unreadMessage = this.state.unreadMessage + 1;
                        this.setState({
                            unreadMessage
                        });
                        this.setStorageItem({ unreadMessage }, LOCAL_STORAGE.GLADLY_CHAT);
                    }

                    const actionInfo = this.state.proActiveChat.campaignOpen
                        ? ended?.[0]?.source === CHAT_ENTRY.timeOut
                            ? anaConsts.LinkData.PROACTIVE_CHAT_SESSION_EXPIRED
                            : anaConsts.LinkData.PROACTIVE_CHAT_SESSION_ENDED
                        : anaConsts.LinkData.CUSTOMER_CHAT_SESSION_ENDED;

                    this.fireAnalytics(
                        {
                            actionInfo,
                            eventStrings: [anaConsts.Event.EVENT_221]
                        },
                        anaConsts.LINK_TRACKING_EVENT
                    );
                }
            });

            Gladly?.on('message:received', msg => {
                if (msg && this.gladRef.current?.classList?.contains('gladly-show')) {
                    const unreadMessage = this.state.unreadMessage + 1;
                    this.setState({
                        unreadMessage
                    });
                    this.setStorageItem({ unreadMessage }, LOCAL_STORAGE.GLADLY_CHAT);
                }
            });

            Gladly?.on('campaign:triggered', this.onCampaignTriggered);
        } catch (error) {
            Sephora.logger.error('Gladly_Events_Failed:', error);
        }
    };

    onApplyCampaign = () => {
        const {
            proActiveChat: { campaignId }
        } = this.state;
        this.props.updateGladChatState({ isChatOpen: true });
        this.validateUserAndSet();
        Gladly.applyCampaign(campaignId);
        this.onCloseCampaign({ unreadMessage: 0 }, true);
        this.fireAnalytics(
            {
                actionInfo: anaConsts.LinkData.PROACTIVE_CHAT_SESSION_STARTED,
                eventStrings: [anaConsts.Event.EVENT_220]
            },
            anaConsts.LINK_TRACKING_EVENT
        );
    };

    onCloseCampaign = (unread = {}, campaignOpen = false) => {
        this.setState({
            ...(unread || {}),
            proActiveChat: {
                showGreeting: false,
                campaignId: '',
                action: null,
                campaignOpen
            }
        });
    };

    showGreeting = (campaignId, action) => {
        this.setState({
            proActiveChat: {
                showGreeting: true,
                campaignId,
                action,
                campaignOpen: false
            }
        });
        this.fireAnalytics(
            {
                actionInfo: anaConsts.LinkData.PROACTIVE_CHAT_SESSION_TRIGGER
            },
            anaConsts.LINK_TRACKING_EVENT
        );
    };

    onCampaignTriggered = data => {
        const campaignData = data[0];

        for (let i = 0; i < campaignData.actions.length; i++) {
            const action = campaignData.actions[i];

            switch (action.type) {
                case 'GREETING':
                    this.showGreeting(campaignData.id, action);

                    break;
                default:
                    break;
            }
        }
    };

    timeOutHandler = conversationEnd => {
        if (!this.conversationTimeout) {
            const diff = Date.now() - conversationEnd;
            const timer = gladChatUtils.getTimer();
            const TIMER = timer * 60 * 1000;
            const remainingTimer = diff > TIMER ? 0 : TIMER - diff;
            this.conversationTimeout = setTimeout(async () => {
                await this.handleClear();
                this.setStorageItem({ conversationEnd: null, unreadMessage: 0 }, LOCAL_STORAGE.GLADLY_CHAT);
                this.validateUserAndSet();
            }, remainingTimer);
        }
    };

    validateUserAndSet = async (cancellationToken = null) => {
        try {
            const login = userUtils.getProfileEmail();
            const firstName = userUtils.getProfileFirstName();

            let gladlyUser = Gladly?.getUser();
            let current = this.getStorageItem(LOCAL_STORAGE.GLADLY_CHAT);

            if (!login && current?.userAuth) {
                await this.handleClear();
                this.handleClose();
                this.props.updateGladChatState({ isChatOpen: false });
                this.setStorageItem(
                    {
                        userAuth: null,
                        isChatOpen: false,
                        conversationEnd: null,
                        unreadMessage: 0,
                        gladToken: null
                    },
                    LOCAL_STORAGE.GLADLY_CHAT
                );
            }

            if (firstName && login && !userUtils.isAnonymousOrRecognizedNotSSI()) {
                if (gladlyUser?.identity && gladlyUser?.identity !== login) {
                    await this.handleClear();
                    this.setStorageItem({ gladToken: null }, LOCAL_STORAGE.GLADLY_CHAT);
                }

                gladlyUser = Gladly?.getUser();
                current = this.getStorageItem(LOCAL_STORAGE.GLADLY_CHAT);

                if (!Sephora.configurationSettings.gladChatConfig?.jwtVerification) {
                    this.handleSetUser(firstName, login, gladlyUser?.identity);
                } else {
                    if (gladlyUser?.type === CHAT_ENTRY.authenticate && !userUtils.isSignedIn()) {
                        await this.handleClear();
                        this.handleSetUser(firstName, login);
                    } else if (!gladlyUser) {
                        let token = login === current?.userAuth ? current?.gladToken : null;
                        try {
                            if (!token) {
                                const biId = userUtils.getBiAccountId();
                                ({ token } = await gladApi.getGladlyToken(biId));
                                this.setStorageItem({ gladToken: token }, LOCAL_STORAGE.GLADLY_CHAT);
                            }

                            Gladly?.setUser({
                                name: firstName,
                                jwt: token
                            });
                        } catch (error) {
                            this.handleSetUser(firstName, login);
                            Sephora.logger.error('Gladly_Identity_API:/generateToken:', error);
                        }
                    }
                }

                this.setStorageItem({ userAuth: login }, LOCAL_STORAGE.GLADLY_CHAT);
            }
        } catch (error) {
            Sephora.logger.error('Gladly_User_Failed:', error);
        } finally {
            if (!cancellationToken || this._pendingShowToken === cancellationToken) {
                this.setState({
                    isHandleShowClicked: false
                });
            }
        }
    };

    waitForUserData = (timeoutMs = 3000) => {
        const isUserDataReady = () => {
            const { profileStatus } = store.getState().auth;
            const login = userUtils.getProfileEmail();
            const firstName = userUtils.getProfileFirstName();

            if (userUtils.isAnonymousOrRecognizedNotSSI({ profileStatus })) {
                return true;
            }

            if (!firstName || !login) {
                return false;
            }

            if (Sephora.configurationSettings.gladChatConfig?.jwtVerification) {
                // Non-BI signed-in users have no beautyInsiderAccount — proceed to the non-JWT fallback path in validateUserAndSet
                return !store.getState().user?.beautyInsiderAccount || !!userUtils.getBiAccountId();
            }

            return true;
        };

        if (isUserDataReady()) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            let resolved = false;

            const finish = () => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timeout);
                    clearInterval(interval);
                    resolve();
                }
            };

            const timeout = setTimeout(finish, timeoutMs);
            const interval = setInterval(() => {
                if (isUserDataReady()) {
                    finish();
                }
            }, 50);
        });
    };

    initializeGladChat = (appId, isChatOpen, conversationEnd) => {
        Gladly?.init({
            appId
        })
            .then(() => {
                this.afterInitilizeGladChat();
                this.props.updateGladChatState({ isChatOpen });

                if (!isChatOpen) {
                    this.handleClose();
                }

                if (conversationEnd) {
                    this.timeOutHandler(conversationEnd);
                }
            })
            .catch(function (error) {
                Sephora.logger.error('Gladly_Initialize_Failed:', error);
            });
    };

    handleSetUser = (firstName, email, identity = false) => {
        !identity &&
            Gladly?.setUser({
                name: firstName,
                email: email
            });
    };

    handleClear = async () => {
        try {
            await Gladly?.clearUser();
        } catch (error) {
            Sephora.logger.error('Gladly_ClearUser_Failed:', error);
        }
    };

    handleClose = () => {
        if (window.Gladly) {
            try {
                this._pendingShowToken = null;
                this.setState({
                    isHandleShowClicked: false
                });
                Gladly?.close();
            } catch (error) {
                Sephora.logger.error('Gladly_Close_Failed:', error);
            }
        } else {
            Sephora.logger.error('Gladly_Is_Not_Available_On_Window');
        }
    };

    handleShow = async () => {
        if (this.state.isHandleShowClicked) {
            return;
        }

        if (window.Gladly) {
            const token = {};
            this._pendingShowToken = token;

            try {
                this.setState({
                    unreadMessage: 0,
                    isHandleShowClicked: true
                });
                await this.waitForUserData();

                if (this._pendingShowToken !== token) {
                    return;
                }

                await this.validateUserAndSet(token);

                if (this._pendingShowToken !== token) {
                    return;
                }

                Gladly?.show();
                this.setStorageItem({ unreadMessage: 0 }, LOCAL_STORAGE.GLADLY_CHAT);
                Sephora.configurationSettings.isGenAIChatEnabledUFE &&
                    store.getState().superChat?.showSuperChat &&
                    store.dispatch(SuperChatActions.showSuperChat(false));
                this.state.proActiveChat.showGreeting && this.onCloseCampaign({}, this.state.proActiveChat.campaignOpen);
            } catch (error) {
                Sephora.logger.error('Gladly_Show_Failed:', error);
            }
        } else {
            Sephora.logger.error('Gladly_Is_Not_Available_On_Window');
        }
    };

    minimizeButtonAction = () => {
        this.handleShow();
        this.fireAnalytics(
            {
                pageName: `${anaConsts.PAGE_NAMES.GLAD_FOOTER_PAGE}:n/a:*`,
                linkData: anaConsts.LinkData.CUSTOMER_CHAT_SESSION_RESUME //prop55
            },
            anaConsts.ASYNC_PAGE_LOAD
        );
    };

    basketCheckoutHeightSmui = isBasketPage => {
        const dataHeightKey = isBasketPage ? 'bottomCostsummaryHeight' : 'bottomStickyHeight';

        return document.body.querySelector(`[data-height="${dataHeightKey}"]`)?.clientHeight || 0;
    };

    render() {
        const { unreadMessage, proActiveChat } = this.state;
        const { isChatOpen } = this.props;
        const btnLabel = getText('buttonLabel');
        const globalStyles = css`
            #custom-gladly-chat-button.gladly-show {
                display: flex;
            }
            ${(isChatOpen || proActiveChat.showGreeting) &&
            css`
                .multi-chat-active {
                    transform: translateY(-${TRANS_FORM_Y}px);
                    transition:
                        bottom 0.3s ease-in-out,
                        transform 0.3s ease-in-out !important;
                    ${mediaQueries.smMax} {
                        bottom: var(--custom-bottom-value, calc(var(--bottomNavHeight, 0px) + ${space[2]}px)) !important;
                    }
                }
            `}
        `;

        return (
            <>
                <Global styles={globalStyles} />
                <div
                    css={[styles.chatEntry]}
                    ref={this.gladChatEntry}
                >
                    {proActiveChat.showGreeting && (
                        <ProactiveGreeting
                            action={proActiveChat.action}
                            onApplyCampaign={this.onApplyCampaign}
                            onClose={this.onCloseCampaign}
                        />
                    )}
                    <button
                        id='custom-gladly-chat-button'
                        onClick={this.minimizeButtonAction}
                        css={[styles.chatButton, !isChatOpen && styles.chatButtonHide]}
                        ref={this.gladRef}
                    >
                        <div css={[styles.chatBox]}>
                            <Media
                                lessThan='md'
                                css={styles.smUIChatLabel}
                            >
                                {unreadMessage > 0 ? <div css={styles.redBox}>{unreadMessage}</div> : null}
                                <Icon
                                    name='gladChat'
                                    size={26}
                                />
                            </Media>
                            <Media
                                greaterThanOrEqual='md'
                                css={[styles.labelContent]}
                            >
                                {unreadMessage > 0 ? <div css={styles.redBox}>{unreadMessage}</div> : null}
                                <Icon
                                    name='gladChat'
                                    size={24}
                                />
                                <span css={styles.chatLabel}>{btnLabel}</span>
                                <Icon
                                    name={'caretUp'}
                                    size={24}
                                />
                            </Media>
                        </div>
                    </button>
                </div>
            </>
        );
    }
}

const styles = {
    chatEntry: {
        position: 'fixed',
        bottom: `var(--custom-bottom-value, calc(var(--bottomNavHeight, 0px) + ${space[2]}px))`,
        right: space[2],
        display: 'flex',
        alignItems: 'center',
        [mediaQueries.md]: {
            bottom: space[4],
            right: space[4]
        }
    },
    chatButton: {
        display: 'none',
        backgroundColor: colors.black,
        color: colors.white,
        borderRadius: radii.full,
        zIndex: 1,
        width: 48,
        height: 48,
        transition: 'bottom 0.3s ease-in-out',
        marginTop: 'auto',
        marginLeft: space[2],
        [mediaQueries.md]: {
            padding: `${space[3]}px ${space[4]}px`,
            width: 258
        },
        [mediaQueries.smMax]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 48px'
        }
    },
    chatButtonHide: {
        display: 'none !important'
    },
    labelContent: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: space[2]
    },
    chatBox: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        justifyContent: 'center',
        width: '100%',
        [mediaQueries.md]: {
            justifyContent: 'unset'
        }
    },
    smUIChatLabel: {
        position: 'relative'
    },
    chatLabel: {
        textAlign: 'center',
        fontSize: fontSizes.md,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        marginRight: 'auto'
    },
    redBox: {
        width: 20,
        height: 20,
        backgroundColor: colors.red,
        borderRadius: radii.full,
        position: 'absolute',
        justifyContent: 'center',
        top: -space[1],
        left: space[2],
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        fontSize: fontSizes.sm,
        fontWeight: fontWeights.bold
    }
};

export default wrapComponent(GladChat, 'GladChat');
